import { languageLocale, t } from './i18n';
import type { CostSource } from './api';
export function formatDate(value?: string | null): string {
  if (!value) return t('time.never');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t('time.never');
  return date.toLocaleDateString(languageLocale());
}

export function formatUsd(value?: number | null, maxDigits = 2): string {
  const n = value ?? 0;
  if (n === 0) return '$0.00';
  const abs = Math.abs(n);
  if (abs >= 1) return `$${n.toFixed(2)}`;
  if (abs >= 0.01) return `$${n.toFixed(Math.min(Math.max(maxDigits, 2), 4))}`;
  // 极小金额：保留有效数字，上限 6 位
  return `$${n.toFixed(Math.min(Math.max(maxDigits, 4), 6))}`;
}

export function formatCostUsd(
  value?: number | null,
  source?: CostSource | string | null,
  maxDigits = 2,
): string {
  const formatted = formatUsd(value, maxDigits);
  return isApproximateCost(source) ? t('common.estimated_cost', { amount: formatted }) : formatted;
}

// Only billing_ledger is an exact debit. Every other non-empty label, including
// values a newer backend may add, is shown as an estimate. A missing label
// predates cost sources and keeps the plain amount.
export function costSourceDescription(source?: CostSource | string | null): string | undefined {
  if (!source || source === 'billing_ledger') return undefined;
  if (source === 'request_price') return t('common.cost_request_price');
  if (source === 'current_pricing_estimate') return t('common.cost_current_pricing_estimate');
  if (source === 'mixed') return t('common.cost_mixed');
  return t('common.cost_estimated');
}

function isApproximateCost(source?: CostSource | string | null): boolean {
  return Boolean(source) && source !== 'billing_ledger';
}

export function deriveSlug(displayName: string): string {
  const base = displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);
  const prefix = base || 'endpoint';
  const suffix = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`)
    .replace(/-/g, '')
    .slice(0, 6);
  return `${prefix}-${suffix}`;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * When to retry, from a Retry-After header (delta-seconds or an HTTP date),
 * in the user's locale. Empty when absent, unparseable or already past, so
 * callers can fall back to wording without a time.
 */
export function retryAtLabel(value?: string): string {
  if (!value) return '';
  const seconds = /^\d+$/.test(value) ? Number(value) : Number.NaN;
  const timestamp = Number.isFinite(seconds) ? Date.now() + seconds * 1000 : Date.parse(value);
  if (!Number.isFinite(timestamp) || timestamp <= Date.now()) return '';
  return new Date(timestamp).toLocaleString(languageLocale());
}
